/**
 * Block parse/serialize/diff utilities.
 *
 * Converts markdown content to/from blocks using `---` (horizontal rule)
 * as the single block separator.  Pure functions — no side effects.
 */

/**
 * Split markdown content into blocks.
 *
 * `---` (horizontal rule on its own line) is the ONLY block separator.
 * Blank lines inside a block are ordinary content, never a block boundary —
 * so a single block spanning multiple paragraphs stays one block
 * (see 19timeline_ops_log: "原文空行/列表不破坏解析").
 *
 * @param {string} content - Raw markdown content
 * @returns {string[]} Array of block content strings (trimmed)
 */
export function parseBlocks(content) {
  if (!content || !content.trim()) return [];

  // --- separator (horizontal rule on its own line)
  if (content.includes('\n---\n')) {
    return content.split('\n---\n').map((b) => b.trim()).filter((b) => b !== '');
  }

  // No separator → the whole content is a single block.
  return [content.trim()];
}

/**
 * Serialize blocks back to markdown.
 * Always joins with `---` separator for clean round-tripping.
 *
 * @param {string[]} blocks - Array of block content strings
 * @returns {string} Full markdown content
 */
export function serializeBlocks(blocks) {
  return blocks
    .map((b) => b.trim())
    .filter((b) => b !== '')
    .join('\n\n---\n\n');
}

/**
 * Extract a preview string from block content.
 * Uses the first line as a title-like summary for short content,
 * or truncates to maxLen characters.
 *
 * @param {string} content - Full block content
 * @param {number} [maxLen=150] - Max preview length in characters
 * @returns {string} Preview text
 */
export function blockPreview(content, maxLen = 150) {
  if (!content) return '';

  // Extract first line (title-like summary)
  const firstLine = content.trim().split('\n')[0].trim();
  if (firstLine.length <= maxLen) return firstLine;

  return firstLine.slice(0, maxLen) + '…';
}

/**
 * Two-level diff of old vs new block arrays.
 *
 * Detection rules:
 * - **Create**: new blocks prepended at head → type "create" for each extra.
 * - **Edit**: same length → compare position-by-position → type "edit".
 * - **Delete**: not logged (intentionally skipped).
 *
 * @param {string[]} oldBlocks - Parsed blocks from previous file content
 * @param {string[]} newBlocks - Parsed blocks from new file content
 * @returns {Array<{type: string, index: number, oldSummary: string|null, newSummary: string|null}>}
 */
export function diffBlocks(oldBlocks, newBlocks) {
  const changes = [];

  if (newBlocks.length > oldBlocks.length) {
    // New blocks prepended at head — treat each extra as a "create"
    const extraCount = newBlocks.length - oldBlocks.length;
    for (let i = 0; i < extraCount; i++) {
      changes.push({
        type: 'create',
        index: i,
        oldSummary: null,
        newSummary: newBlocks[i],
      });
    }
  } else if (newBlocks.length === oldBlocks.length) {
    // Same length — compare position-by-position
    for (let i = 0; i < newBlocks.length; i++) {
      if (newBlocks[i] !== oldBlocks[i]) {
        changes.push({
          type: 'edit',
          index: i,
          oldSummary: oldBlocks[i],
          newSummary: newBlocks[i],
        });
      }
    }
  }

  // Deletions are intentionally skipped

  return changes;
}

/**
 * Compute which blocks are "unsynced" against a base content, using a
 * two-state multiset diff (order-independent, occurrence-count only).
 *
 * For each current block (in display order), if its content still has an
 * unused occurrence in `baseContent`'s blocks, it's considered synced;
 * otherwise it's unsynced.  This deliberately yields only two states —
 * it can't tell "new" apart from "deleted then re-added".
 *
 * @param {string[]} blocks - Current (display-order) block array
 * @param {string|null} baseContent - Last-synced full-file content, or null
 * @returns {Set<number>} indices of `blocks` that are unsynced
 */
export function computeUnsyncedIndices(blocks, baseContent) {
  if (!baseContent) return new Set();
  const baseBlocks = parseBlocks(baseContent);
  const counts = new Map();
  for (const b of baseBlocks) {
    counts.set(b, (counts.get(b) || 0) + 1);
  }
  const unsynced = new Set();
  blocks.forEach((b, i) => {
    const c = counts.get(b) || 0;
    if (c > 0) {
      counts.set(b, c - 1);
    } else {
      unsynced.add(i);
    }
  });
  return unsynced;
}
